import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { StatementView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { statementKindKeys, formatMoney } from './billing';
import { statementCsv } from './statementCsv';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export interface BillingStatementProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}/billing`. */
  base: string;
  accountId: string;
  /** Changes whenever the ledger changed, so the statement is read again. */
  refreshToken: unknown;
}

const todayInUtc = (): string => new Date().toISOString().slice(0, 10);

/** The account as the ledger stood at the end of a chosen day, with a running balance. */
export function BillingStatement({
  locale,
  base,
  accountId,
  refreshToken,
}: BillingStatementProps): React.JSX.Element {
  const [asOf, setAsOf] = useState(todayInUtc);
  const [statement, setStatement] = useState<StatementView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  /** Fetches the statement again through the audited export route and saves it as a CSV file. */
  async function download(): Promise<void> {
    setErrorKey(null);
    try {
      const exported = await apiRequest<StatementView>(
        `${base}/accounts/${accountId}/statement/export?asOf=${asOf}`,
      );
      const csv = statementCsv(exported, locale, new Date().toISOString());
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement-${exported.asOf}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  useEffect(() => {
    let current = true;
    setStatement(null);
    setErrorKey(null);
    if (asOf === '') return undefined;
    apiRequest<StatementView>(`${base}/accounts/${accountId}/statement?asOf=${asOf}`)
      .then((loaded) => {
        if (current) setStatement(loaded);
      })
      .catch((error: unknown) => {
        if (current) setErrorKey(errorKeyOf(error));
      });
    return () => {
      current = false;
    };
  }, [accountId, asOf, base, refreshToken]);

  return (
    <Box component="section" aria-labelledby="statement-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h3" id="statement-title" gutterBottom>
        {translate(locale, 'billing.statement.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'billing.statement.note')}
      </Typography>
      <TextField
        size="small"
        type="date"
        label={translate(locale, 'billing.statement.asOf')}
        value={asOf}
        onChange={(event) => {
          setAsOf(event.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ mb: 2, mr: 2 }}
      />
      <Button
        variant="outlined"
        disabled={asOf === '' || statement === null}
        onClick={() => {
          void download();
        }}
        sx={{ mb: 2 }}
      >
        {translate(locale, 'billing.reports.export')}
      </Button>
      {errorKey !== null && <Alert severity="error">{translate(locale, errorKey)}</Alert>}
      {errorKey === null && statement === null && asOf !== '' && (
        <Typography>{translate(locale, 'billing.statement.loading')}</Typography>
      )}
      {statement !== null && (
        <>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 3, mb: 2 }}>
            {(
              [
                ['billing.position.overdue', statement.position.overdueMinor],
                ['billing.position.upcoming', statement.position.upcomingMinor],
                ['billing.position.credit', statement.position.unallocatedCreditMinor],
                ['billing.statement.closing', statement.closingBalanceMinor],
              ] as const
            ).map(([labelKey, amountMinor]) => (
              <Box key={labelKey}>
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, labelKey)}
                </Typography>
                <Typography component="p" sx={{ fontWeight: 600 }}>
                  {formatMoney(amountMinor, statement.currency, locale)}
                </Typography>
              </Box>
            ))}
          </Stack>
          {statement.lines.length === 0 ? (
            <Typography>{translate(locale, 'billing.statement.empty')}</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label={translate(locale, 'billing.statement.title')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate(locale, 'billing.col.date')}</TableCell>
                    <TableCell>{translate(locale, 'billing.col.type')}</TableCell>
                    <TableCell>{translate(locale, 'billing.col.description')}</TableCell>
                    <TableCell align="right">{translate(locale, 'billing.col.change')}</TableCell>
                    <TableCell align="right">{translate(locale, 'billing.col.balance')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statement.truncated && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        {translate(locale, 'billing.statement.opening')}
                        <Typography variant="caption" component="span" color="text.secondary">
                          {' · '}
                          {translate(locale, 'billing.statement.truncated')}
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        {formatMoney(statement.openingBalanceMinor, statement.currency, locale)}
                      </TableCell>
                    </TableRow>
                  )}
                  {statement.lines.map((line) => (
                    <TableRow key={`${line.kind}-${line.id}`}>
                      <TableCell>{formatCalendarDate(line.postedOn, locale)}</TableCell>
                      <TableCell>{translate(locale, statementKindKeys[line.kind])}</TableCell>
                      <TableCell>
                        {line.description ?? ''}
                        {line.effectiveOn !== null && (
                          <Typography variant="caption" component="span" color="text.secondary">
                            {line.description === null ? '' : ' · '}
                            {formatCalendarDate(line.effectiveOn, locale)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(line.effectMinor, statement.currency, locale)}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(line.balanceMinor, statement.currency, locale)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {statement.openCharges.length > 0 && (
            <Box sx={{ mt: 2, overflowX: 'auto' }}>
              <Typography variant="subtitle2" component="h4" gutterBottom>
                {translate(locale, 'billing.statement.openCharges')}
              </Typography>
              <Table size="small" aria-label={translate(locale, 'billing.statement.openCharges')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate(locale, 'billing.col.dueOn')}</TableCell>
                    <TableCell>{translate(locale, 'billing.col.description')}</TableCell>
                    <TableCell align="right">
                      {translate(locale, 'billing.col.outstanding')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statement.openCharges.map((charge) => (
                    <TableRow key={charge.id}>
                      <TableCell>{formatCalendarDate(charge.dueOn, locale)}</TableCell>
                      <TableCell>{charge.description}</TableCell>
                      <TableCell align="right">
                        {formatMoney(charge.outstandingMinor, statement.currency, locale)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
